import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import Theme from 'assets/themes/Theme.Common';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { compact, omit } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import AccountReceivableLifecycleStatuses, { AccountReceivableStatuses } from 'models/enums/AccountReceivableStatuses';
import accountReceivableDeleteApi from 'models/finance/accountReceivable/delete/Api.AccountReceivable.Delete';
import AccountReceivable from 'models/finance/accountReceivable/Model.AccountReceivable';
import accountReceivableSaveApi from 'models/finance/accountReceivable/save/Api.AccountReceivable.Save';
import accountReceivableCommentDeleteApi from 'models/finance/accountReceivableComment/delete/Api.AccountReceivableComment.Delete';
import AccountReceivableComment from 'models/finance/accountReceivableComment/Model.AccountReceivableComment';
import accountReceivableCommentSaveApi from 'models/finance/accountReceivableComment/save/Api.AccountReceivableComment.Save';
import Statement from 'models/sales/statement/Model.Statement';
import { useEffect, useState } from 'react';
import { financeAccountReceivableIndexRoute } from './Finance.AccountReceivable.Index';

export enum AccountReceivableAction {
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
  IMPORT = 'import',
  INFO = 'info',
}

export enum AccountReceivableInfoTab {
  INFO = 'info',
  DOCUMENTS = 'documents',
  NOTES = 'notes',
}

export type VerifyAccountReceivableAction = Utils.VerifyExtends<
  Module.Finance.AccountReceivable.Actions,
  Utils.ValueOf<typeof AccountReceivableAction>
>;

export type AccountReceivableForm = Omit<Api.Finance.AccountReceivable.Save.IRequest, 'accountReceivableComments'> & {
  chequeFile: Hooks.Input.File.Info;
  accountReceivableComments: Array<
    Api.Finance.AccountReceivableComment.Save.IRequest & {
      tempCreatedAtUtcUnix: number;
      createdAtUtc: string;
      user: User;
    }
  >;
  statementCandidates: Array<Model.IStatementScore & { isSelected: boolean }>;
};

function getAccountReceivablePermissions(permissions: ReturnType<typeof useAuthStore>['permissions']) {
  return {
    canView:
      permissions?.CREATE_ACCOUNTRECEIVABLE ||
      permissions?.EDIT_ACCOUNTRECEIVABLE ||
      permissions?.DELETE_ACCOUNTRECEIVABLE ||
      permissions?.IMPORT_ACCOUNTRECEIVABLE,
    canCreate: permissions?.CREATE_ACCOUNTRECEIVABLE,
    canEdit: permissions?.EDIT_ACCOUNTRECEIVABLE,
    canDelete: permissions?.DELETE_ACCOUNTRECEIVABLE,
    canImport: permissions?.IMPORT_ACCOUNTRECEIVABLE,
  };
}

export default function useAccountReceivable() {
  const { translate } = getLocales();
  const { user, permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Finance.AccountReceivable.Params, Module.Finance.AccountReceivable.Query>({
    route: financeAccountReceivableIndexRoute,
  });
  const access = getAccountReceivablePermissions(permissions);

  const listApi = useApi(
    {
      action: AccountReceivable.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        dealerId: user?.dealers?.map((d) => d.id) ?? [],
        distributorId: user?.distributorId ? [user.distributorId] : undefined,
        statusCode: [
          AccountReceivableLifecycleStatuses.ADVISED_MATCHED,
          AccountReceivableLifecycleStatuses.ADVISED_UNMATCHED,
          AccountReceivableLifecycleStatuses.RECEIVED,
        ],
      },
      body: pageRouter.query.list,
      wait: !access.canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );
  const findApi = useApi(
    {
      action: AccountReceivable.find,
      wait: !access.canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );
  const selected = pageRouter.params.id ? findApi.payload : undefined;

  const dealerListApi = useApi({
    action: Dealer.list,
    wait: !access.canView,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
    },
  });
  const statementListApi = useApi(
    {
      action: Statement.list,
      wait: !access.canView || !dealerListApi?.payload?.data,
      default: {
        dealerId: compact(dealerListApi?.payload?.data?.map((it) => it.id)),
      },
    },
    [dealerListApi?.payload?.data]
  );

  const userListApi = useApi({
    action: User.list,
    default: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: !access.canView,
  });

  const commentsApi = useApi({
    action: AccountReceivableComment.list,
    wait: true,
  });

  useEffect(() => {
    if (selected?.id) commentsApi.execute({ accountReceivableId: selected.id });
    else commentsApi.reset(true);
  }, [selected?.id]);

  const deleteApi = useApi({
    action: accountReceivableDeleteApi,
    callback: () => {
      pageRouter.updateParams({});
      listApi.execute((body) => body);
    },
    wait: true,
  });

  const saveApi = useApi({
    action: accountReceivableSaveApi,
    callback: () => {
      pageRouter.updateParams({});
      listApi.execute((body) => body);
    },
    wait: true,
  });

  //#region Statuses
  const allStatusesWithoutProcessed = omit(AccountReceivableStatuses, ['PROCESSED']);
  function getStatusTag(status?: string) {
    const defaultData = {
      backgroundColor: Theme.Color.warning,
      color: Theme.Color.neutralLight,
      label: translate(status) || 'N/A',
    };
    switch (status) {
      case AccountReceivableLifecycleStatuses.ADVISED_MATCHED:
      case AccountReceivableLifecycleStatuses.ADVISED_UNMATCHED:
        return {
          ...defaultData,
          backgroundColor: Theme.Color.primary,
        };
      case AccountReceivableLifecycleStatuses.PROCESSED:
        return { ...defaultData, backgroundColor: Theme.Color.success };
      case AccountReceivableLifecycleStatuses.RECEIVED:
      default:
        return defaultData;
    }
  }
  //#endregion

  return {
    canView: access.canView,
    pageRouter,
    listApi,
    findApi,
    selected,
    dealerListApi,
    statementListApi,
    userListApi,
    commentsApi,
    deleteApi,
    saveApi,
    permissions,
    access,
    allStatusesWithoutProcessed,
    views: {
      [AccountReceivableAction.CREATE]:
        pageRouter.params.action === AccountReceivableAction.CREATE && access.canCreate && !selected,
      [AccountReceivableAction.DELETE]:
        pageRouter.params.action === AccountReceivableAction.DELETE && selected && access.canDelete,
      [AccountReceivableAction.EDIT]:
        pageRouter.params.action === AccountReceivableAction.EDIT && access.canEdit && !!selected,
      [AccountReceivableAction.IMPORT]: pageRouter.params.action === AccountReceivableAction.IMPORT && access.canImport,
      [AccountReceivableAction.INFO]: pageRouter.params.action === AccountReceivableAction.INFO && !!selected,
    },
    getStatusTag,
  };
}

export function useAccountReceivableProvider() {
  return useDataProvider<ReturnType<typeof useAccountReceivable>>();
}

export function useAccountReceivableComments() {
  const { lang } = getLocales();
  const { selected: accountReceivable, commentsApi } = useAccountReceivable();
  const auth = useAuthStore();
  const [action, setAction] = useState<'edit' | 'delete'>();
  const reloadComments = () => {
    commentsApi.execute((b) => b);
    form.reload();
    setAction(null);
  };
  const form = useForm<Utils.FormData<Model.IAccountReceivableComment>>({
    default: {
      accountReceivableId: accountReceivable?.id,
      userId: auth.userId,
      comment: undefined,
    },
    validation: (data, errors) => {
      if (!data.comment) errors.comment = lang.required;
    },
    onSubmit: async (data) => {
      const res = await accountReceivableCommentSaveApi({
        ...data,
        modifiedAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      });

      if (res.status !== HttpStatus.ok) addLog({ error: lang.unknownError });
      else {
        addLog({ success: lang.saveSuccess });
        reloadComments();
      }
    },
  });

  useEffect(() => {
    if (accountReceivable?.id) form.update({ accountReceivableId: accountReceivable.id });
  }, [accountReceivable?.id]);

  const deleteApi = useApi({
    action: accountReceivableCommentDeleteApi,
    callback: reloadComments,
    wait: true,
  });

  return {
    form,
    deleteApi,
    comments: commentsApi.payload?.data ?? [],
    isLoadingComments: commentsApi.isExecuting,
    commentsApi,
    action,
    setAction,
  };
}
